import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import * as XLSX from 'xlsx';
import '../../css/Pages.css';
import ConferenceRegistrationListing from '../../components/Register/ConferenceRegistration/ConferenceRegistrationListing';
import ConferenceRegistrationForm from '../../components/Register/ConferenceRegistration/ConferenceRegistrationForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';

const ConferenceRegistration = () => {
    const toast = useRef(null);

    const today = new Date().toISOString().split('T')[0];
    const initialForm = { ParticipantName: '', ConferenceName: '', ArticleName: '', Website: '', StartDate: today, EndDate: today, AttachmentUrl: '' };

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'ConferenceName', direction: 'asc' });
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [editId, setEditId] = useState(null);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        toast.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Phiên đăng nhập hết hạn hoặc không có quyền truy cập!', life: 3000 });
        setTimeout(() => {
            localStorage.removeItem('user');
            localStorage.removeItem('accessToken');
            window.location.href = '/login';
        }, 1500);
    }, []);

    const fetchData = useCallback(async (isBackground = false) => {
        if (!isBackground) setIsLoading(true);
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                handleLogout();
                if (!isBackground) setIsLoading(false);
                return;
            }
            const currentUser = JSON.parse(userStr);
            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
                ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/conferenceregistration?userId=${currentUser.Id}&isAdmin=${isManager}&_t=${Date.now()}`,
                {
                    method: 'GET',
                    headers: getAuthHeaders(),
                    cache: 'no-store'
                }
            );

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                const result = await response.json();
                setData(result);
            }
        } catch (error) {
            console.error(error);
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể tải dữ liệu: ' + error.message });
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const pollInterval = setInterval(() => {
            fetchData(true);
        }, 15000);

        return () => clearInterval(pollInterval);
    }, [fetchData]);

    useEffect(() => {
        let result = [...data];

        if (searchQuery) {
            result = result.filter(item =>
                (item.ConferenceName && item.ConferenceName.toLowerCase().includes(searchQuery)) ||
                (item.ArticleName && item.ArticleName.toLowerCase().includes(searchQuery))
            );
        }

        if (sortConfig !== null) {
            result.sort((a, b) => {
                const valA = (a[sortConfig.key] || '').toString().trim();
                const valB = (b[sortConfig.key] || '').toString().trim();
                const comparison = valA.localeCompare(valB, 'vi', { sensitivity: 'base' });
                return sortConfig.direction === 'asc' ? comparison : -comparison;
            });
        }

        setFilteredData(result);
    }, [data, searchQuery, sortConfig]);

    const handleSearch = (e) => {
        setSearchQuery(e.target.value.toLowerCase());
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};
            if (!currentUser || !currentUser.Id) return handleLogout();

            const userId = currentUser.Id;
            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
                ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/conferenceregistration?isAdmin=${isManager}&userId=${userId}&_t=${Date.now()}`, {
                method: 'PATCH',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id, status: newStatus }),
                cache: 'no-store'
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? 'Gửi yêu cầu duyệt thành công!' : 'Hủy yêu cầu duyệt thành công!'
                });
                fetchData(true);
            } else {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Thao tác thất bại' });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ' });
        }
    };

    const handleBulkStatusChange = async (ids, newStatus) => {
        if (newStatus === 'Đã gửi duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
            if (invalidItems.length > 0) {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt để có thể thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Đang chỉnh sửa') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã gửi duyệt');
            if (invalidItems.length > 0) {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã gửi duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};
            const userId = currentUser ? currentUser.Id : null;
            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
                ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/conferenceregistration?isAdmin=${isManager}&userId=${userId}&_t=${Date.now()}`, {
                    method: 'PATCH',
                    headers: {
                        ...getAuthHeaders(),
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ id, status: newStatus }),
                    cache: 'no-store'
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? `Đã gửi yêu cầu duyệt ${ids.length} mục!` : `Đã hủy gửi duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (item) => {
        setEditId(item.Id);
        setFormData({ ...item });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        const item = data.find(x => x.Id === id);
        if (item?.Status === 'Đã duyệt' || item?.Status === 'Đã gửi duyệt') {
            toast.current.show({ severity: 'error', summary: 'Từ chối', detail: 'Không thể xóa hồ sơ đã khóa' });
            return;
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: 'Bạn có chắc chắn muốn xóa đăng ký này?',
            accept: async () => {
                try {
                    const currentUser = JSON.parse(localStorage.getItem('user'));
                    if (!currentUser) {
                        handleLogout();
                        return;
                    }
                    const userId = currentUser.Id;

                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/conferenceregistration?id=${id}&userId=${userId}&_t=${Date.now()}`, {
                        method: 'DELETE',
                        headers: getAuthHeaders(),
                        cache: 'no-store'
                    });

                    if (response.status === 401 || response.status === 403) {
                        handleLogout();
                        return;
                    }

                    if (response.ok) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa hồ sơ' });
                        fetchData(true);
                    } else {
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Xóa thất bại' });
                    }
                } catch (error) {
                    toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ' });
                }
            }
        });
    };

    const handleBulkDelete = (ids) => {
        const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
        if (invalidItems.length > 0) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa hàng loạt',
            message: `Bạn có chắc chắn muốn xóa ${ids.length} đăng ký đã chọn?`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const currentUser = JSON.parse(localStorage.getItem('user')) || {};
                    const userId = currentUser ? currentUser.Id : null;

                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/conferenceregistration?id=${id}&userId=${userId}&_t=${Date.now()}`, {
                            method: 'DELETE',
                            headers: getAuthHeaders(),
                            cache: 'no-store'
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: `Đã xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi xóa thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const closeModal = () => { setIsModalOpen(false); setFormData(initialForm); setEditId(null); };

    const exportToExcel = () => {
        const dataToExport = filteredData;

        if (dataToExport.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!' });
        }

        const excelData = dataToExport.map((item, index) => ({
            'STT': index + 1,
            'Người tham dự': item.ParticipantName || '',
            'Tên Hội nghị - Hội thảo': item.ConferenceName || '',
            'Tên bài báo - tham luận': item.ArticleName || '',
            'Website Hội nghị': item.Website || '',
            'Ngày bắt đầu': item.StartDate ? new Date(item.StartDate).toLocaleDateString('vi-VN') : '',
            'Ngày kết thúc': item.EndDate ? new Date(item.EndDate).toLocaleDateString('vi-VN') : '',
            'Trạng thái': item.Status || 'Đang chỉnh sửa',
            'Người tạo': item.CreatedByEmail || ''
        }));

        excelData.push({
            'STT': '',
            'Người tham dự': 'TỔNG CỘNG:',
            'Tên Hội nghị - Hội thảo': `${dataToExport.length} Đăng ký`,
            'Tên bài báo - tham luận': '',
            'Website Hội nghị': '',
            'Ngày bắt đầu': '',
            'Ngày kết thúc': '',
            'Trạng thái': '',
            'Người tạo': ''
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "DangKyThamDuHoiNghi");

        const wscols = [
            { wch: 5 },
            { wch: 25 },
            { wch: 50 },
            { wch: 50 },
            { wch: 25 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 25 }
        ];
        worksheet['!cols'] = wscols;

        XLSX.writeFile(workbook, `DanhSachDangKyThamDuHoiNghi_${today}.xlsx`);
    };

    return (
        <div className="page-container">
            <Toast ref={toast} />
            <div className="page-header">
                <div className="header-title">
                    <h2>ĐĂNG KÝ THAM DỰ HỘI NGHỊ - HỘI THẢO</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                <button className="btn-add-new" onClick={() => setIsModalOpen(true)} style={{ margin: 0 }}>
                    <i className="fa-solid fa-plus"></i> Thêm mới
                </button>
                <button
                    className="btn-add-new"
                    style={{ backgroundColor: '#1a9953', margin: 0 }}
                    onClick={exportToExcel}
                >
                    <i className="fa-solid fa-file-excel"></i> Xuất Excel
                </button>
            </div>

            <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '5px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                    <p className="sub-title" style={{ margin: 0 }}>DANH SÁCH ĐĂNG KÝ THAM DỰ HỘI NGHỊ - HỘI THẢO</p>
                    <div className="search-wrapper" style={{ position: 'relative', width: '100%', maxWidth: '350px' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '12px', color: '#888' }}></i>
                        <input type="text" placeholder="Tìm kiếm" className="form-input" style={{ width: '100%', paddingLeft: '35px' }} value={searchQuery} onChange={handleSearch} />
                    </div>
                </div>
            </div>

            <ConferenceRegistrationListing
                data={filteredData}
                searchQuery={searchQuery}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSendSubmit={(id) => handleStatusChange(id, 'Đã gửi duyệt')}
                onCancelSubmit={(id) => handleStatusChange(id, 'Đang chỉnh sửa')}
                onBulkSend={(ids) => handleBulkStatusChange(ids, 'Đã gửi duyệt')}
                onBulkCancel={(ids) => handleBulkStatusChange(ids, 'Đang chỉnh sửa')}
                onBulkDelete={handleBulkDelete}
                isLoading={isLoading}
                toast={toast}
                sortConfig={sortConfig}
                requestSort={(key) => {
                    let direction = 'asc';
                    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
                    setSortConfig({ key, direction });
                }}
            />

            <ConferenceRegistrationForm
                isOpen={isModalOpen}
                onClose={closeModal}
                formData={formData}
                setFormData={setFormData}
                isEditing={!!editId}
                isReadOnly={formData.Status === 'Đã duyệt' || formData.Status === 'Đã gửi duyệt'}
                toast={toast}
                fetchData={fetchData}
            />
        </div>
    );
};

export default ConferenceRegistration;